import React, { Component } from 'react';
import { Action, bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';

import { getModifierKeys, isMac } from '@paragon/utilities';

import {
  copySelectedAnnotationsToClipboard,
  cutSelectedAnnotationsToClipboard,
  deleteSelectedItems,
  deselectAllOnActivePage,
  pasteSelectedAnnotationsToClipboard,
  redoLastAnnotationChange,
  selectAllEditableAnnotationsOnActivePage,
  undoLastAnnotationChange,
} from '../actions/annotations';
import {
  selectFirstDocument,
  selectLastDocument,
  selectNextDocument,
  selectNextPage,
  selectPreviousDocument,
  selectPreviousPage,
} from '../actions/documents';
import { getSelectedDocument } from '../selectors/documents';
import { RootState } from '../store';

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = StateProps & DispatchProps;

export class UnconnectedKeyboardInputListener extends Component<Props> {
  componentDidMount() {
    document.addEventListener('keydown', this.onKeyPress, true);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onKeyPress, true);
  }

  onKeyPress = (event: KeyboardEvent) => {
    const { keyCode } = event;
    const { ctrlKey } = getModifierKeys(event);

    // Keyboard shortcuts can't be entered while a dialog window is open
    if (this.props.dialogIsOpen) {
      return;
    }

    // Keyboard shortcuts can't be entered while a text input is focused
    const targetTag = (event.target! as Element).tagName.toLowerCase();
    if (targetTag === 'input' || targetTag === 'textarea') {
      return;
    }

    // Ctrl+C: Copy
    if (ctrlKey && keyCode === 67) {
      event.preventDefault();
      this.props.copySelectedAnnotationsToClipboard();
    }

    // Ctrl+X: Cut
    else if (ctrlKey && keyCode === 88) {
      event.preventDefault();
      this.props.cutSelectedAnnotationsToClipboard();
    }

    // Ctrl+V: Paste
    else if (ctrlKey && keyCode === 86) {
      event.preventDefault();
      this.props.pasteSelectedAnnotationsToClipboard();
    }

    // Ctrl+A: Select all
    else if (keyCode === 65 && ctrlKey) {
      event.preventDefault();
      this.props.selectAllEditableAnnotationsOnActivePage();
    }

    // Esc: Deselect all
    else if (keyCode === 27) {
      this.props.deselectAllOnActivePage();
    }

    // PgUp: Jump to first document
    else if (keyCode === 33) {
      event.preventDefault();
      this.props.selectFirstDocument();
    }

    // PgDn: Jump to last document
    else if (keyCode === 34) {
      event.preventDefault();
      this.props.selectLastDocument();
    }

    // Ctrl+Z: Undo
    else if (keyCode === 90 && ctrlKey) {
      this.props.undoLastAnnotationChange();
    }

    // Ctrl+Y: Redo
    else if (keyCode === 89 && ctrlKey) {
      this.props.redoLastAnnotationChange();
    }

    // Delete: Delete selected items
    else if (keyCode === 46 || (isMac() && keyCode === 8)) {
      this.props.deleteSelectedItems();
    }

    // Left Arrow: Go to previous page
    else if (keyCode === 37) {
      event.preventDefault();
      this.props.selectPreviousPage();
    }

    // Right Arrow: Go to next page
    else if (keyCode === 39) {
      event.preventDefault();
      this.props.selectNextPage();
    }

    // Up Arrow: Go to previous document
    else if (keyCode === 38) {
      event.preventDefault();
      this.props.selectPreviousDocument();
    }

    // Down Arrow: Go to next document
    else if (keyCode === 40) {
      event.preventDefault();
      this.props.selectNextDocument();
    }

    // Plus/Equals: Scale up selected items
    else if (keyCode === 187) {
      this.props.scaleUpSelectedItems(event);
    }

    // Minus: Scale down selected items
    else if (keyCode === 189) {
      this.props.scaleDownSelectedItems(event);
    }
  };

  render() {
    return <span />;
  }
}

const mapStateToProps = (state: RootState) => ({
  activePdf: getSelectedDocument(state),
  dialogIsOpen: state.popup.popupName != null,
});

function mapDispatchToProps(dispatch: Dispatch<Action>) {
  const boundActions = bindActionCreators(
    {
      cutSelectedAnnotationsToClipboard,
      copySelectedAnnotationsToClipboard,
      pasteSelectedAnnotationsToClipboard,
      selectAllEditableAnnotationsOnActivePage,
      deselectAllOnActivePage,
      selectFirstDocument,
      selectLastDocument,
      redoLastAnnotationChange,
      undoLastAnnotationChange,
      deleteSelectedItems,
      selectPreviousPage,
      selectNextPage,
      selectPreviousDocument,
      selectNextDocument,
    },
    dispatch,
  );

  return {
    ...boundActions,

    scaleUpSelectedItems: (event: KeyboardEvent) => window.drawingCanvas.scaleUpTenPercent(event),
    scaleDownSelectedItems: (event: KeyboardEvent) => window.drawingCanvas.scaleDownTenPercent(event),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(UnconnectedKeyboardInputListener);
